import { useState, useRef, useEffect } from "react";
import { Play, Pause, RotateCcw, Volume2, VolumeX, Captions, FileText, ArrowRight, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LecturerVideoCard({ lesson, onContinue, allowSkip = true, autoplay = "muted", continueLabel = "Continue to Hands-On Task" }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(autoplay === "muted");
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(lesson?.duration_est || 0);
  const [rate, setRate] = useState(1);
  const [showCC, setShowCC] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);
  const [started, setStarted] = useState(false);

  const audioSrc = lesson?.audio_url ? `${BACKEND}${lesson.audio_url}` : null;
  const cues = lesson?.captions || [];
  const active = cues.find((c) => t >= c.start && t < c.end) || (t > 0 ? cues[cues.length - 1] : cues[0]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !audioSrc) return;
    a.playbackRate = rate;
    a.muted = muted;
    if (autoplay !== "require") {
      a.play().then(() => { setPlaying(true); setStarted(true); }).catch(() => setPlaying(false));
    }
  // eslint-disable-next-line
  }, [audioSrc]);

  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) { a.muted = muted; a.play(); setPlaying(true); setStarted(true); }
    else { a.pause(); setPlaying(false); }
  };
  const replay = () => { const a = audioRef.current; if (!a) return; a.currentTime = 0; a.play(); setPlaying(true); setStarted(true); };
  const changeRate = () => { const r = rate === 1 ? 1.25 : rate === 1.25 ? 1.5 : rate === 1.5 ? 0.75 : 1; setRate(r); if (audioRef.current) audioRef.current.playbackRate = r; };
  const pct = dur ? Math.min(100, (t / dur) * 100) : 0;

  if (!lesson) return null;
  return (
    <div className="rounded-xl border border-[#0078d4]/30 bg-card overflow-hidden shadow-sm" data-testid="lecturer-video-card">
      <div className="px-4 py-2 bg-[#0078d4]/5 border-b border-border flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-widest text-[#0078d4] font-semibold">Your Azure Instructor</span>
        <span className="text-xs text-muted-foreground ml-auto">{lesson.technology}</span>
      </div>
      <div className="relative bg-slate-900 aspect-video flex items-center justify-center overflow-hidden">
        <img src={lesson.lecturer?.avatar} alt={lesson.lecturer?.name} data-testid="lecturer-avatar"
          className={`h-full w-full object-cover object-top transition-transform duration-700 ${playing ? "scale-105" : "scale-100"}`} />
        {playing && <span className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] text-white/90 bg-black/40 px-2 py-1 rounded-full"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> Speaking…</span>}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
          <div className="text-white text-sm font-semibold">{lesson.lecturer?.name}</div>
          <div className="text-white/70 text-[11px]">{lesson.title}</div>
          {showCC && active && (
            <div className="mt-2 text-center text-white text-sm bg-black/50 rounded px-3 py-1.5 leading-snug" data-testid="lecturer-caption">{active.text}</div>
          )}
        </div>
        {!started && autoplay === "require" && (
          <button onClick={toggle} data-testid="lecturer-play-overlay" className="absolute inset-0 grid place-items-center bg-black/40">
            <span className="h-16 w-16 rounded-full bg-[#0078d4] grid place-items-center text-white"><Play className="h-8 w-8 ml-1" /></span>
          </button>
        )}
      </div>
      {audioSrc && (
        <audio ref={audioRef} src={audioSrc} data-testid="lecturer-audio"
          onTimeUpdate={(e) => setT(e.target.currentTime)}
          onLoadedMetadata={(e) => setDur(e.target.duration || lesson.duration_est)}
          onEnded={() => setPlaying(false)} />
      )}
      <div className="h-1 bg-muted"><div className="h-full bg-[#0078d4] transition-[width]" style={{ width: `${pct}%` }} /></div>
      <div className="flex items-center gap-1 px-3 py-2 flex-wrap">
        <Button size="icon" variant="ghost" onClick={toggle} data-testid="lecturer-playpause">{playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</Button>
        <Button size="icon" variant="ghost" onClick={replay} data-testid="lecturer-replay"><RotateCcw className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => { setMuted(!muted); if (audioRef.current) audioRef.current.muted = !muted; }} data-testid="lecturer-mute">{muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}</Button>
        <Button size="sm" variant="ghost" onClick={changeRate} data-testid="lecturer-speed" className="gap-1"><Gauge className="h-4 w-4" />{rate}x</Button>
        <Button size="icon" variant={showCC ? "secondary" : "ghost"} onClick={() => setShowCC(!showCC)} data-testid="lecturer-cc"><Captions className="h-4 w-4" /></Button>
        <Button size="icon" variant={showTranscript ? "secondary" : "ghost"} onClick={() => setShowTranscript(!showTranscript)} data-testid="lecturer-transcript-toggle"><FileText className="h-4 w-4" /></Button>
        <span className="text-xs text-muted-foreground ml-1">{Math.round(t)}s / {Math.round(dur)}s</span>
        <div className="ml-auto flex gap-2">
          {allowSkip && onContinue && <Button size="sm" variant="ghost" onClick={onContinue} data-testid="lecturer-skip">Skip</Button>}
          {onContinue && <Button size="sm" className="gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={onContinue} data-testid="lecturer-continue">{continueLabel}<ArrowRight className="h-4 w-4" /></Button>}
        </div>
      </div>
      {showTranscript && (
        <div className="px-4 py-3 border-t border-border bg-secondary/30 text-sm text-muted-foreground max-h-40 overflow-y-auto" data-testid="lecturer-transcript">
          <div className="text-xs font-semibold text-foreground mb-1">Transcript</div>
          {lesson.transcript || lesson.script}
        </div>
      )}
      {!audioSrc && (
        <div className="px-4 py-2 text-xs text-amber-600 border-t border-border" data-testid="lecturer-no-audio">Video unavailable — continue with the transcript below.</div>
      )}
    </div>
  );
}
